import React, { useEffect, useState } from 'react';

import { Autocomplete } from './Autocomplete';
import {
  filterOptions, findSelected, labelForSelected, shouldClear, valueFromSelectedOption,
} from './optionAutocompleteHelpers';


/**
 * Value/label bridge over the presentation-only `Autocomplete` primitive. The
 * primitive speaks option OBJECTS + an external `query`; this component exposes a
 * simple `value` (id string) / `onChange(nextValue)` contract and owns the typed
 * query, client-side filtering, clear-on-empty, and reopen-shows-all behaviour
 * via the pure helpers in optionAutocompleteHelpers.js (unit-tested in the node env).
 */
const OptionAutocomplete = React.forwardRef((props, ref) => {
  const {
    options = [],
    value = '',
    onChange,
    placeholder,
    invalid = false,
    disabled = false,
    emptyText,
    id,
    className,
    footer,
    loading = false,
  } = props;

  const selected = findSelected(options, value);
  const [query, setQuery] = useState(() => labelForSelected(selected));

  // Resync the input text whenever the external value changes, or when the
  // matching option's label loads in (async options). Keyed on value + the
  // resolved label so unrelated re-renders don't clobber in-progress typing.
  useEffect(() => {
    setQuery(labelForSelected(selected));
    // Depend on value + the resolved label only — NOT the whole `selected`
    // object (a fresh reference each render) — so an unrelated re-render never
    // clobbers the user's in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selected?.label]);

  const filtered = filterOptions(options, query, selected);

  const handleQueryChange = (text) => {
    setQuery(text);
    if (shouldClear(text)) {
      onChange?.('');
    }
  };

  return (
    <Autocomplete
      ref={ref}
      id={id}
      query={query}
      value={selected}
      invalid={invalid}
      disabled={disabled}
      className={className}
      emptyText={emptyText}
      footer={footer}
      loading={loading}
      options={filtered}
      placeholder={placeholder}
      getOptionKey={(o) => o.value}
      getOptionLabel={(o) => o.label}
      onQueryChange={handleQueryChange}
      renderOption={(o) => <span className="truncate text-[13px] text-ink">{o.label}</span>}
      onChange={(option) => {
        onChange?.(valueFromSelectedOption(option));
        setQuery(option.label);
      }}
    />
  );
});

OptionAutocomplete.displayName = 'OptionAutocomplete';

export { OptionAutocomplete };
export default OptionAutocomplete;
